import { useEffect, useState } from "react";
import axios from 'axios';
import OrganizationComponentStats from "../../components/OrganizationComponentStats/OrganizationComponentStats";
import CircularProgress from '@mui/material/CircularProgress';
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Alert from '@mui/material/Alert';
import { CompetenceStatsResponse, MatrixReportInfoResponse } from "../../components/OrganizationComponentStats/OrganizationComponentStats.types";
import styles from "./Reports.module.scss";
import OrganizationComponentStatsCharts from "../../components/OrganizationComponentStatsCharts/OrganizationComponentStatsCharts";
import OrganizationComponentStatsChartsExcel from "../../components/OrganizationComponentStats/OrganizationComponentStatsExcel";
import { MenuItem, Select, FormControl, InputLabel, SelectChangeEvent, Box, Autocomplete, TextField } from "@mui/material";

export const Reports = () => {
    const organizationEntityId = 'organization'
    const { user } = useSelector((state: RootState) => state.auth);
    const { t } = useTranslation();
    const [competenceStatsResponse, setCompetenceStatsResponse] = useState<CompetenceStatsResponse | null>(null);
    const [matrixReportInfoResponse, setMatrixReportInfoResponse] = useState<MatrixReportInfoResponse | null>(null);
    const [matrixReportInfoEntityId, setMatrixReportInfoEntityId] = useState<string>(organizationEntityId);
    const [entityMatrices, setEntityMatrices] = useState<{label: string, id: string}[]>([]);
    const [chosenMatrix, setChosenMatrix] = useState<{label: string, id: string} | null>(null);
    const [loader, setLoader] = useState<boolean>(true);
    const [isMissingEntities, setIsMissingEntities] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)

    const handleChange = (event: SelectChangeEvent) => {
      setMatrixReportInfoEntityId(event.target.value as string);
    };

    useEffect(() => {
      const fetchData = async () => {
        setLoader(true);
        try {
          const response = await axios.get(`${window.APP_CONFIG.REACT_APP_API_BASEURL}/competence_tests/matrix-report-info/organizational-matrix-access`, {
            headers: {
              Authorization: `Bearer ${user.access}`
            }
          });
          const result: MatrixReportInfoResponse = await response.data;
          if(result.entities.length === 0) {
            setIsMissingEntities(true)
          }
        setMatrixReportInfoResponse(result);
          setLoader(false);
        } catch (err: any) {
          if (axios.isCancel(err) || err.name === 'AbortError') return;
          console.error('Api error:', err);
          setLoader(false);
          setIsError(true)
        }
      }
  
      fetchData();
    }, [user.access]);


    useEffect(() => {
      if(matrixReportInfoResponse) {

        // check if user is admin, if not we have a contract that there will be the only one entity in array
        // so we need to set user id manually
        if(matrixReportInfoResponse.entities.length === 1 && matrixReportInfoResponse.entities[0].entity_id !== null ) {
          setMatrixReportInfoEntityId(matrixReportInfoResponse.entities[0].entity_id)
        }

        const entityMatrix = matrixReportInfoResponse.entities.find(
          item => item.entity_id === matrixReportInfoEntityId || 
                (item.entity_id === null && matrixReportInfoEntityId === organizationEntityId)
        );

        if (entityMatrix && entityMatrix.matrices) {
          const formattedMatrices = entityMatrix.matrices.map((item : {matrix_name: string, matrix_pk: number}) => ({
            label: item.matrix_name,
            id: item.matrix_pk + ''
          }));
          setEntityMatrices(formattedMatrices);
          setChosenMatrix(null);
        }
      }
    }, [matrixReportInfoEntityId, matrixReportInfoResponse]);

    useEffect(() => {
      if (!chosenMatrix) {
        return;
      }
      const controller = new AbortController();
      
      const fetchData = async () => {

        setLoader(true);
        try {
          const params = matrixReportInfoEntityId === organizationEntityId ? {
            matrix_pk: chosenMatrix.id,
          } : {
            matrix_pk: chosenMatrix.id,
            user_id: matrixReportInfoEntityId
          }
          const response = await axios.get(`${window.APP_CONFIG.REACT_APP_API_BASEURL}/competence_tests/organization_test_results`, {
            signal: controller.signal,
            params,
            headers: {
              Authorization: `Bearer ${user.access}`
            }
          });
          const result: CompetenceStatsResponse = await response.data;
          setCompetenceStatsResponse(result);
          setLoader(false);
        } catch (err: any) {
          if (axios.isCancel(err) || err.name === 'AbortError') return;
          console.error('Api error:', err);
          setLoader(false);
          setIsError(true)
        }
      }
  
      fetchData();
      return () => controller.abort();
    }, [user.access, chosenMatrix]);

    if(isError) {
      return <Alert severity="error">{t('reports.error')}</Alert>;
    }

    if(isMissingEntities) {
      return <Alert severity="info">{t('reports.missingReports')}</Alert>;
    }

    let content;

    if (loader) {
      content = <CircularProgress />;
    } else if (!chosenMatrix) {
      content = <Alert severity="info">{t('reports.selectMatrix')}</Alert>;
    } else if (competenceStatsResponse?.competence_stats[1] === 200) {
      content = <Alert severity="info">{t('reports.noDataorganizationWideCompetencies')}</Alert>;
    } else if (competenceStatsResponse !== null) {
      content = (
        <>
          <OrganizationComponentStats competenceStatsResponse={competenceStatsResponse} />
          <OrganizationComponentStatsCharts competenceStatsResponse={competenceStatsResponse} />
          <OrganizationComponentStatsChartsExcel token={user.access} matrixId={chosenMatrix.id} />
        </>
      );
    }

    return (
      <>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          gap: 2, 
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'flex-start'
        }}>
          {matrixReportInfoResponse && matrixReportInfoResponse.isAdmin && matrixReportInfoResponse.entities.length > 0 && (  
            <Box sx={{ minWidth: 120, width: { xs: '100%', sm: 300 } }}>
              <FormControl fullWidth>
                <InputLabel id="entity-select-label">{t("reports.user")}</InputLabel>
                <Select
                  labelId="entity-select-label"
                  id="entityIdSelect"
                  value={matrixReportInfoEntityId}
                  label={t("reports.user")}
                  onChange={handleChange}
                >
                  {matrixReportInfoResponse.entities.map(entity => {
                    const entityId = entity.entity_id === null ? organizationEntityId : entity.entity_id;
                    return ( 
                      <MenuItem key={entityId} value={entityId}>{entity.entity_name}</MenuItem>
                    );
                  })}  
                </Select>
              </FormControl>
            </Box>
          )}
            
          <Box sx={{ minWidth: 120, width: { xs: '100%', sm: 300 } }}>
            <Autocomplete
              disablePortal
              options={entityMatrices}
              id="chosen-matrix"
              noOptionsText={t("reports.noOption")}
              sx={{ width: '100%' }}
              renderInput={(params) => <TextField {...params} label={t("reports.competencyModel")} />}
              blurOnSelect
              value={chosenMatrix}
              onChange={(_event, newValue) => {
                setChosenMatrix(newValue);
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />      
          </Box>
        </Box>
        
        <div className={styles.wrapper}>
          {content}
        </div>
      </>
    );
};
